import {
  EditorQueries,
  ERROR_MOVE_CANNOT_DROP,
  ERROR_MOVE_INCOMING_PARENT,
  ERROR_MOVE_TO_DESCENDANT,
  Node,
} from '@candulabs/craft';

import { BuiltInComponents } from 'craft/constants';
import { getResolver } from 'craft/hooks';
import { CanduCraftIndicator } from 'craft/types';

const isFormInput = (inputName: string) => {
  return [
    'FormTextInput',
    'FormTextarea',
    'FormSelectInput',
    'FormCheckboxList',
    'FormMultiSelectCardGroup',
    'FormMultiSelectCard',
  ].includes(inputName);
};

const errorMatches = (message: string, error: string) => message.endsWith(error);

const nodeName = (query: EditorQueries, node: Node) => {
  if (!node?.data?.name) {
    return 'these nodes';
  }

  // we can't use nodeApi directly to get displayName as it only works for nodes in the document
  const resolver = getResolver(query, node.data.name);

  const resolverDisplayName = resolver?.craft?.displayName;
  if (typeof resolverDisplayName === 'function') {
    const res = resolverDisplayName(node, query.node);
    if (res) {
      return res;
    }
  } else if (typeof resolverDisplayName === 'string') {
    return resolverDisplayName;
  }

  return node.data.name;
};

export const getErrorText = (
  indicator: CanduCraftIndicator,
  query: EditorQueries,
  draggedNode?: Node,
) => {
  const { error, placement } = indicator;

  if (!error) {
    return false;
  }

  // @ts-expect-error
  const { message } = error;
  const parentNode = query.node(placement?.parent?.id);

  if (!draggedNode || !parentNode.node) {
    return message;
  }

  const { name: draggedName } = draggedNode.data;
  const { name: droppedName } = parentNode.node.data;

  // Refer to src/core/constants/errors.ts for a complete list
  switch (true) {
    case errorMatches(message, ERROR_MOVE_INCOMING_PARENT):
    case errorMatches(message, ERROR_MOVE_CANNOT_DROP):
      if (draggedName === BuiltInComponents.Flow) {
        return `You can't place Flow inside a Flow`;
      }

      if (draggedName === BuiltInComponents.Column) {
        return 'Column may only be placed inside a Column Group';
      }

      if (droppedName === BuiltInComponents.Columns) {
        return 'Only Column nodes may be placed inside a Column Group';
      }

      if (
        isFormInput(draggedName) &&
        droppedName !== BuiltInComponents.Form &&
        !query
          .node(parentNode.node.id)
          .ancestors()
          .find(
            (ancestorId) =>
              (query.node(ancestorId).get().data.name as BuiltInComponents) ===
              BuiltInComponents.FormStep,
          )
      ) {
        // only show this error if we are actually trying to drop outside the Form components and its children
        return 'Form inputs may only be placed inside a Form';
      }
      return `You can't place ${nodeName(query, draggedNode)} inside ${nodeName(
        query,
        parentNode.node,
      )}`;

    case errorMatches(message, ERROR_MOVE_TO_DESCENDANT):
      return `You can't place ${nodeName(query, draggedNode)} inside itself`;
    default:
      return message;
  }
};
